import React, { useCallback, useEffect, useState } from "react";
import { Cpu, Sparkles, Layers3, TrendingUp, Gauge, Ruler, Lock, Waves, Clock, Scale, Mountain, Shield } from "lucide-react";
import { toast } from "sonner";
import { fetchSgStrategy, updateSgStrategy, fetchSgStrategies, updateSgStrategies } from "@/lib/smartgrid_api";

const C = {
    panel: "#0F1620", border: "#1B2A3A", text: "#E6EDF6", sub: "#A7B4C6",
    dim: "#6F7D92", accent: "#2EE6D6", bull: "#2BE38B", bear: "#FF5C6C", warn: "#FFB020",
};

const Toggle = ({ on, onClick, disabled }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-40"
        style={{ background: on ? C.accent : "#26364A" }}
        data-state={on ? "on" : "off"}
    >
        <span className="inline-block h-4 w-4 rounded-full bg-white transition-transform"
            style={{ transform: on ? "translateX(18px)" : "translateX(2px)" }} />
    </button>
);

/**
 * Dependency badge on a toggle row. `active` = the dependency currently
 * applies (e.g. this toggle needs the Overlay ON but it is OFF) → warn tint;
 * otherwise a dim informational chip.
 */
const Badge = ({ label, active }) => (
    <span
        className="text-[9px] font-mono px-1 py-[1px] rounded whitespace-nowrap"
        style={active
            ? { color: C.warn, background: "rgba(255,176,32,0.10)", border: "1px solid rgba(255,176,32,0.35)" }
            : { color: C.dim, background: "#0B1219", border: `1px solid ${C.border}` }}
    >
        {label}
    </span>
);

const Line = ({ icon: Icon, title, desc, badges, children }) => (
    <div className="flex items-start justify-between gap-3 py-2.5 border-b" style={{ borderColor: "#13202D" }}>
        <div className="flex items-start gap-2.5 min-w-0">
            <Icon size={15} color={C.accent} className="mt-0.5 shrink-0" />
            <div className="min-w-0">
                <div className="text-[12px] flex items-center gap-1.5 flex-wrap" style={{ color: C.text }}>
                    <span>{title}</span>
                    {(badges || []).map((b) => <Badge key={b.label} label={b.label} active={b.active} />)}
                </div>
                {desc && <div className="text-[10px] leading-snug" style={{ color: C.dim }}>{desc}</div>}
            </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">{children}</div>
    </div>
);

/** Section micro-header: groups related toggles under an uppercase label. */
const Section = ({ icon: Icon, title, note, first }) => (
    <div className={`flex items-center gap-2 pb-1 ${first ? "" : "mt-4 pt-3 border-t"}`}
        style={first ? {} : { borderColor: C.border }}>
        <Icon size={13} color={C.accent} />
        <span className="text-[11px] uppercase tracking-[0.14em]" style={{ color: C.sub }}>{title}</span>
        {note && (
            <span className="text-[9px] px-1.5 py-0.5 rounded font-mono"
                style={{ color: C.accent, background: "rgba(46,230,214,0.1)" }}>{note}</span>
        )}
    </div>
);

const StrSelect = ({ value, options, onChange, disabled }) => (
    <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="bg-[#0B0F14] border rounded px-1.5 py-1 text-[11px] font-mono disabled:opacity-40"
        style={{ borderColor: C.border, color: C.text }}
    >
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
);

const NumSelect = ({ value, options, onChange, disabled, suffix = "" }) => (
    <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        className="bg-[#0B0F14] border rounded px-1.5 py-1 text-[11px] font-mono disabled:opacity-40"
        style={{ borderColor: C.border, color: C.text }}
    >
        {options.map((o) => <option key={o} value={o}>{o}{suffix}</option>)}
    </select>
);

const PROT_LABELS = {
    fastmove_guard: "Fast-move guard (stale-quote protection)",
    pump_guard: "Pump guard (don't sell into a leader pump)",
    liq_brake: "Liquidation-cascade brake",
    basis_gate: "Cross-exchange basis gate",
    ev_gate: "Expected-value gate",
    macro_influence: "Macro trend + candlestick influence",
};

export default function SmartGridStrategy() {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);
    const [prot, setProt] = useState(null);

    const load = useCallback(async () => {
        try { setData(await fetchSgStrategy()); } catch (_) { /* transient */ }
        try { setProt(await fetchSgStrategies()); } catch (_) { /* transient */ }
    }, []);

    useEffect(() => { load(); const id = setInterval(load, 4000); return () => clearInterval(id); }, [load]);

    const toggleProt = async (k) => {
        const next = !(prot?.toggles?.[k] ?? true);
        try {
            setProt(await updateSgStrategies({ [k]: next }));
            toast.success(`${PROT_LABELS[k]} ${next ? "enabled" : "DISABLED"}`);
        } catch (e) {
            toast.error(`Toggle failed: ${e?.response?.data?.detail || e.message}`);
        }
    };

    const cfg = data?.config || {};
    const eff = data?.effective || {};
    const liveLocked = !!data?.live_locked;
    const aiCtl = cfg.strategy_ai_controlled !== false;
    const overlayOn = !!cfg.strategy_enabled;
    const zoneOn = !!cfg.strat_zone_entry;
    const zoneConf = data?.zone_confidence || [];

    const patch = async (fields) => {
        setSaving(true);
        // optimistic
        setData((d) => d ? { ...d, config: { ...d.config, ...fields } } : d);
        try {
            const r = await updateSgStrategy(fields);
            setData(r);
            if (Array.isArray(r?.auto_changes) && r.auto_changes.length > 0) {
                const parts = r.auto_changes.map((c) => `${c.key} → ${String(c.to)}`);
                toast.info(`Auto-adjusted: ${parts.join(", ")}`);
            }
        } catch (e) {
            toast.error(`Strategy save failed: ${e?.response?.data?.detail || e.message}`);
            load();
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="panel" data-testid="sg-strategy-panel">
            <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: C.border }}>
                <Sparkles size={14} color={C.accent} />
                <span className="text-[11px] uppercase tracking-[0.16em]" style={{ color: C.sub }}>
                    AI Strategy Overlay
                </span>
                <span className="text-[9px] px-1.5 py-0.5 rounded font-mono ml-1"
                    style={{ color: C.accent, background: "rgba(46,230,214,0.1)" }}>
                    paper / testnet only
                </span>
                <div className="ml-auto flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-[0.12em]" style={{ color: C.dim }}>Enable</span>
                    <Toggle on={overlayOn} disabled={saving || liveLocked}
                        onClick={() => patch({ strategy_enabled: !overlayOn })} />
                </div>
            </div>

            {liveLocked && (
                <div className="px-4 py-2 flex items-center gap-2 text-[11px]"
                    style={{ background: "rgba(255,92,108,0.08)", color: C.bear, borderBottom: `1px solid ${C.border}` }}
                    data-testid="sg-strategy-live-lock">
                    <Lock size={12} /> LIVE mode — strategy overlay is disabled. The bot runs the neutral ladder with real funds; switch to PAPER to experiment.
                </div>
            )}

            <div className="p-4">
                {/* ---- Master ---- */}
                <Section icon={Cpu} title="Master" first />
                <Line icon={Cpu} title="AI-controlled"
                    desc="AI picks slice / double-down from the live market regime & confidence."
                    badges={[
                        { label: "needs Overlay ON", active: !overlayOn },
                        { label: "paces Slice & DD", active: aiCtl && overlayOn },
                    ]}>
                    <Toggle on={aiCtl} disabled={saving || !overlayOn}
                        onClick={() => patch({ strategy_ai_controlled: !aiCtl })} />
                </Line>

                {/* ---- Entry strategies ---- */}
                <Section icon={Waves} title="Entry strategies" />
                <Line icon={Waves} title="Support-zone entries (Dalton)"
                    desc="A SEPARATE strategy that rests committed bids at strong support zones ALONGSIDE the normal grid — it does not replace or suspend the ladder. TP under the next resistance; stop ratchets under minor supports, never below entry+fees. Hold while avg confidence ≥ threshold, else shift the buy down."
                    badges={[
                        { label: "runs alongside the ladder", active: zoneOn },
                        { label: "adds committed zone bids", active: zoneOn },
                    ]}>
                    <NumSelect value={cfg.strat_zone_conf_threshold || 65} options={[55, 60, 65, 70, 75]}
                        disabled={saving || !zoneOn}
                        onChange={(v) => patch({ strat_zone_conf_threshold: v })} />
                    <Toggle on={zoneOn}
                        disabled={saving || liveLocked}
                        onClick={() => patch({ strat_zone_entry: !zoneOn })} />
                </Line>

                <Line icon={Layers3} title="Slice orders"
                    desc={aiCtl
                        ? "AI sizes slices DYNAMICALLY from the volatility percentile (2–4×); the select applies in manual mode."
                        : "Split each rung into N equidistant slices (average in on chop/thin books)."}
                    badges={[
                        { label: "needs Overlay ON", active: !overlayOn },
                        { label: "AI-paced", active: aiCtl && overlayOn },
                    ]}>
                    {aiCtl && eff.slice_count > 1 && (
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded"
                            style={{ background: "#13202D", color: C.accent }}>AI: ×{eff.slice_count}</span>
                    )}
                    <NumSelect value={cfg.strat_slice_count || 2} options={[2, 3, 4]} suffix="×"
                        disabled={saving || !overlayOn || aiCtl}
                        onChange={(v) => patch({ strat_slice_count: v })} />
                    <Toggle on={aiCtl ? (eff.slice_count > 1) : !!cfg.strat_slice_enabled}
                        disabled={saving || !overlayOn || aiCtl}
                        onClick={() => patch({ strat_slice_enabled: !cfg.strat_slice_enabled })} />
                </Line>

                <Line icon={TrendingUp} title="Double-down top"
                    desc={aiCtl
                        ? "AI sizes concurrency DYNAMICALLY from conviction (confidence + macro trend + EV, 1–4); the select applies in manual mode."
                        : "Pyramid extra rungs into the top of the ladder during a bull run."}
                    badges={[
                        { label: "needs Overlay ON", active: !overlayOn },
                        { label: "vetoed on Donchian down-break", active: false },
                    ]}>
                    {aiCtl && eff.double_down && (
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded"
                            style={{ background: "#13202D", color: C.warn }}>AI: ×{eff.double_down_max}</span>
                    )}
                    <NumSelect value={cfg.strat_double_down_max || 2} options={[1, 2, 3, 4]}
                        disabled={saving || !overlayOn || aiCtl}
                        onChange={(v) => patch({ strat_double_down_max: v })} />
                    <Toggle on={aiCtl ? !!eff.double_down : !!cfg.strat_double_down_enabled}
                        disabled={saving || !overlayOn || aiCtl}
                        onClick={() => patch({ strat_double_down_enabled: !cfg.strat_double_down_enabled })} />
                </Line>

                <Line icon={Gauge} title="Dynamic position sizing"
                    desc="Throttle buy aggression by AI confidence & macro-bear score.">
                    <Toggle on={cfg.strat_dynamic_sizing !== false} disabled={saving}
                        onClick={() => patch({ strat_dynamic_sizing: !(cfg.strat_dynamic_sizing !== false) })} />
                </Line>

                {/* ---- Execution & quoting ---- */}
                <Section icon={Clock} title="Execution & quoting" />
                <Line icon={Ruler} title="Adaptive grid spacing"
                    desc="Widen in trends, tighten in chop — regime-conditioned.">
                    <Toggle on={cfg.strat_adaptive_spacing !== false} disabled={saving}
                        onClick={() => patch({ strat_adaptive_spacing: !(cfg.strat_adaptive_spacing !== false) })} />
                </Line>

                <Line icon={Clock} title="Execution algo (TWAP / VWAP)"
                    desc="Institutional child-order pacing: slice & double-down rungs release on a time clock (TWAP) or only as the market trades enough volume to absorb them (VWAP participation). Base rungs, zones and the knife-catch always place immediately."
                    badges={[
                        { label: "needs Overlay ON", active: !overlayOn },
                        { label: "paces Slice & DD only", active: (cfg.strat_exec_algo || "off") !== "off" },
                    ]}>
                    <StrSelect value={cfg.strat_exec_algo || "off"} options={["off", "twap", "vwap"]}
                        disabled={saving || !overlayOn}
                        onChange={(v) => patch({ strat_exec_algo: v })} />
                </Line>

                <Line icon={Mountain} title="Bear-posture top-gap shift"
                    desc="In CAPITULATION posture the whole buy ladder drops extra grid cells below price so rungs rest under the crash instead of chasing it. OFF pins the top rung to the first grid line under price in every posture. (Defensive posture no longer shifts the ladder — only spacing/size/depth change.)"
                    badges={[
                        { label: "needs Overlay ON", active: !overlayOn },
                        { label: "capitulation only", active: false },
                        ...((eff.extra_top_gaps || 0) > 0
                            ? [{ label: `shifting now: +${eff.extra_top_gaps} cell${eff.extra_top_gaps > 1 ? "s" : ""}`, active: true }]
                            : []),
                    ]}>
                    <Toggle on={cfg.strat_top_gap_enabled !== false} disabled={saving || !overlayOn}
                        onClick={() => patch({ strat_top_gap_enabled: !(cfg.strat_top_gap_enabled !== false) })} />
                </Line>

                <Line icon={Scale} title="Avellaneda-Stoikov quoting"
                    desc="Market-maker inventory model: as held BTC builds past target, buy quotes skew deeper below market and spacing widens with volatility (closed-form reservation price + optimal spread). Bounded, re-quoted at most once a minute."
                    badges={[{ label: "needs Overlay ON", active: !overlayOn }]}>
                    <Toggle on={!!cfg.strat_as_quoting} disabled={saving || !overlayOn}
                        onClick={() => patch({ strat_as_quoting: !cfg.strat_as_quoting })} />
                </Line>

                {/* ---- Trend & protections ---- */}
                <Section icon={Shield} title="Trend & protections" note="gates: supervisor · all modes" />
                <Line icon={Mountain} title="Donchian / Turtle trend filter"
                    desc="Turtle rules on 1m bars: a confirmed 55-bar down-breakout (ADX trend) halves buy aggression and vetoes pyramiding; a 20-bar up-breakout adds a pyramid slot."
                    badges={[
                        { label: "needs Overlay ON", active: !overlayOn },
                        { label: "gates Double-down", active: false },
                    ]}>
                    <Toggle on={cfg.strat_trend_filter !== false} disabled={saving || !overlayOn}
                        onClick={() => patch({ strat_trend_filter: !(cfg.strat_trend_filter !== false) })} />
                </Line>

                {/* Supervisor-level protections & gates — the strategies added
                    with the Binance leader-venue integration. Always-on unless
                    the operator disables them here (persisted). They ride the
                    supervisor, not the overlay, so no Overlay badge. */}
                <div data-testid="sg-protections">
                    {Object.keys(PROT_LABELS).map((k) => (
                        <Line key={k} icon={Cpu} title={PROT_LABELS[k]}
                            desc={prot?.descriptions?.[k] || ""}>
                            <Toggle on={prot?.toggles?.[k] ?? true} disabled={!prot}
                                onClick={() => toggleProt(k)} />
                        </Line>
                    ))}
                    <div className="mt-1.5 text-[10px]" style={{ color: C.dim }}>
                        Thresholds for the guards live in the Leader-Venue Guards panel (auto-tuned or manual).
                        Disabling a protection is logged to the AI action feed and persisted.
                    </div>
                </div>

                {/* Effective state — only what deviates from the neutral ladder
                    is chipped; everything else is noise. */}
                <div className="mt-3 rounded-md p-2.5" style={{ background: "#0B1219", border: `1px solid ${C.border}` }}>
                    <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono">
                        <span className="uppercase tracking-[0.12em]" style={{ color: C.dim }}>Effective:</span>
                        <span className="px-1.5 py-0.5 rounded" style={{ background: "#13202D", color: eff.enabled ? C.bull : C.dim }}>
                            {eff.enabled ? "overlay ON" : "neutral"}
                        </span>
                        {eff.slice_count > 1 && (
                            <span className="px-1.5 py-0.5 rounded" style={{ background: "#13202D", color: C.accent }}>
                                slice ×{eff.slice_count}
                            </span>
                        )}
                        {!!eff.double_down && (
                            <span className="px-1.5 py-0.5 rounded" style={{ background: "#13202D", color: C.warn }}>
                                double-down ×{eff.double_down_max || 1}
                            </span>
                        )}
                        {!!eff.zone_entry && (
                            <span className="px-1.5 py-0.5 rounded" style={{ background: "#13202D", color: C.bull }}>
                                zones ON
                            </span>
                        )}
                        {(eff.extra_top_gaps || 0) > 0 && (
                            <span className="px-1.5 py-0.5 rounded" style={{ background: "#13202D", color: C.warn }}
                                title="Bear-posture top-gap shift: the whole buy ladder rests this many grid cells lower than the classic anchor.">
                                top gap +{eff.extra_top_gaps}
                            </span>
                        )}
                        {eff.posture && eff.posture !== "neutral" && (
                            <span className="px-1.5 py-0.5 rounded" style={{ background: "#13202D", color: eff.posture === "aggressive" ? C.bull : C.warn }}>
                                posture: {eff.posture}
                            </span>
                        )}
                        {eff.exec_algo && eff.exec_algo !== "off" && (
                            <span className="px-1.5 py-0.5 rounded" style={{ background: "#13202D", color: C.accent }}>
                                {String(eff.exec_algo).toUpperCase()}
                            </span>
                        )}
                        {data?.regime && (
                            <span className="px-1.5 py-0.5 rounded" style={{ background: "#13202D", color: C.sub }}>
                                regime: {data.regime}
                            </span>
                        )}
                    </div>
                    {eff.reason && <div className="mt-1.5 text-[10px]" style={{ color: C.dim }}>{eff.reason}</div>}
                    {zoneOn && zoneConf.length > 0 && (
                        <div className="mt-1.5 flex items-center gap-2 flex-wrap text-[10px] font-mono"
                            data-testid="sg-zone-confidence">
                            <span className="uppercase tracking-[0.12em]" style={{ color: C.dim }}>Zone conf:</span>
                            {zoneConf.map((z) => (
                                <span key={z.zone_id} className="px-1.5 py-0.5 rounded"
                                    style={{
                                        background: "#13202D",
                                        color: z.decision === "hold" ? C.bull
                                            : z.decision === "shift" ? C.bear : C.warn,
                                    }}
                                    title={`avg ${z.avg} · ${z.samples} ticks · ${z.bounces} bounces · ${z.tails} tails`}>
                                    {z.zone_id} {z.avg}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
